import { RefObject, useLayoutEffect, useState } from "react";

type ScrollOverflow = {
  /** Content extends beyond the left edge (i.e. the element is scrolled). */
  left: boolean;

  /** Content extends beyond the right edge. */
  right: boolean;

  /** Content extends beyond the top edge (i.e. the element is scrolled). */
  top: boolean;

  /** Content extends beyond the bottom edge. */
  bottom: boolean;

  /** Width taken by the vertical scrollbar; 0 when absent or overlaid. */
  scrollbarWidth: number;

  /** Height taken by the horizontal scrollbar; 0 when absent or overlaid. */
  scrollbarHeight: number;
};

const NO_OVERFLOW: ScrollOverflow = {
  left: false,
  right: false,
  top: false,
  bottom: false,
  scrollbarWidth: 0,
  scrollbarHeight: 0,
};

const overflowsEqual = (a: ScrollOverflow, b: ScrollOverflow) =>
  a.left === b.left &&
  a.right === b.right &&
  a.top === b.top &&
  a.bottom === b.bottom &&
  a.scrollbarWidth === b.scrollbarWidth &&
  a.scrollbarHeight === b.scrollbarHeight;

/** Parses a computed CSS length like "1px"; jsdom reports "" for unset. */
const cssPixels = (value: string) => {
  const pixels = Number.parseFloat(value);
  return Number.isNaN(pixels) ? 0 : pixels;
};

/**
 * Tracks whether the scrollable element behind `ref` has content extending
 * beyond each of its edges, updating as it scrolls or as the element or its
 * content resizes.
 *
 * The target element must mount together with the calling component: the ref
 * is read once per mount, so a conditionally-rendered target would need a
 * callback ref instead.
 */
export function useScrollOverflow(
  ref: RefObject<HTMLElement | null>
): ScrollOverflow {
  const [overflow, setOverflow] = useState(NO_OVERFLOW);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return undefined;
    }

    const update = () => {
      // `offsetWidth`/`offsetHeight` span the border box while `clientWidth`/
      // `clientHeight` exclude borders and scrollbars, so the difference
      // minus the borders is the space the scrollbars occupy.
      const style = getComputedStyle(el);
      const horizontalBorders =
        cssPixels(style.borderLeftWidth) + cssPixels(style.borderRightWidth);
      const verticalBorders =
        cssPixels(style.borderTopWidth) + cssPixels(style.borderBottomWidth);

      // `scrollLeft > 0` assumes LTR (it goes negative in RTL). The 1px
      // tolerances absorb fractional layout sizes.
      const next = {
        left: el.scrollLeft > 0,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
        top: el.scrollTop > 0,
        bottom: el.scrollTop + el.clientHeight < el.scrollHeight - 1,
        scrollbarWidth: el.offsetWidth - el.clientWidth - horizontalBorders,
        scrollbarHeight: el.offsetHeight - el.clientHeight - verticalBorders,
      };
      setOverflow((prev) => (overflowsEqual(prev, next) ? prev : next));
    };

    update();
    el.addEventListener("scroll", update, { passive: true });

    // Observing the first child as well is required: when its content grows
    // (e.g. rows load), only the scroll dimensions change, not the element's
    // own box.
    const observer = new ResizeObserver(update);
    observer.observe(el);
    if (el.firstElementChild) {
      observer.observe(el.firstElementChild);
    }

    return () => {
      el.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [ref]);

  return overflow;
}
